import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { FlexibleImageAvatar } from '../flexible-image-avatar/FlexibleImageAvatar';
import { nullSafeGet, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { getProtectedImageUriForSupplier } from '../../utils/FileAccessUtils';
import { Button, Col, Popconfirm, Row } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { dateFormatter, DATE_FORMAT } from '../../utils/DataFormatterUtils';
import AddPartsModal from './AddPartsModal';

require('./PartsWithQuantityRowDisplay.less');

const PartsWithQuantityRowDisplay = ({
	partWithServiceCall,
	onConfirmRemove,
	successCallback,
	onCreatePartPurchaseRequest,
}): React.ReactElement => {
	const [updateQuantityModalVisible, setUpdateQuantityModalVisible] = useState(false);

	const showUpdateQuantityModal = useCallback(() => setUpdateQuantityModalVisible(true), []);
	const hideUpdateQuantityModal = useCallback(() => setUpdateQuantityModalVisible(false), []);

	const partInServiceCall = useMemo(() => partWithServiceCall.part, [partWithServiceCall]);
	const serviceCall = useMemo(() => partWithServiceCall.serviceCall, [partWithServiceCall]);

	const part = useMemo(
		() =>
			partInServiceCall.partsPerStockLocation
				? partInServiceCall.partsPerStockLocation.part
				: partInServiceCall.part,
		[partInServiceCall]
	);

	const checkAndOpenUpdateQuantityModal = useCallback(
		(e) => {
			stopDefaultEvents(e);
			partInServiceCall.partsPerStockLocation
				? showUpdateQuantityModal()
				: alert('Unable to edit quantity. Please remove and add again!');
		},
		[partInServiceCall, showUpdateQuantityModal]
	);

	const previewImage = useMemo(
		() =>
			nullSafeGet('images', part) && part.images.length > 0
				? part.images.map((locImg) => {
						const [imgFileId, imgFileName] = locImg.split('/');
						return getProtectedImageUriForSupplier(imgFileId, imgFileName, 1200, 800);
				  })[0]
				: null,
		[part]
	);

	const onCreatePR = useCallback(
		(...args) => {
			hideUpdateQuantityModal();
			onCreatePartPurchaseRequest && onCreatePartPurchaseRequest(...args);
		},
		[hideUpdateQuantityModal, onCreatePartPurchaseRequest]
	);

	return (
		<div
			className="partPerStockLocationRowDisplay"
			style={{ justifyContent: 'space-between', marginBottom: '16px' }}
		>
			<div className="partPerStockLocationRowDisplay">
				<div style={{ marginRight: 24 }}>
					{previewImage ? (
						<FlexibleImageAvatar
							displayName={nullSafeGet('name', part)}
							showPopover={false}
							popoverText={nullSafeGet('name', part)}
							squareMode={true}
							width="120px"
							height="80px"
							imageUrl={previewImage}
						/>
					) : (
						<FlexibleImageAvatar
							displayName={nullSafeGet('name', part)}
							showPopover={false}
							popoverText={nullSafeGet('name', part)}
							squareMode={true}
							width="120px"
							height="80px"
							imageUrl="https://s3.amazonaws.com/emptor-data-assets/images/empty-state/icons8-screw-filled-100.png"
						/>
					)}
				</div>
				<div>
					<div className="partPerStockLocationRowDisplay__rowTitle">
						<Ellipsis tooltip={true} length={56} className={'pb-0.5'}>
							{nullSafeGet('name', part)}
						</Ellipsis>
					</div>
					{nullSafeGet('partNumber', part) && (
						<div className="partPerStockLocationRowDisplay__rowSubtitle">
							<div className="partPerStockLocationRowDisplay__rowInlineGroup">
								Part Number : {nullSafeGet('partNumber', part)}
							</div>
						</div>
					)}
					{nullSafeGet('manufacturerName', part) && (
						<div className="partPerStockLocationRowDisplay__rowSubtitle">
							{nullSafeGet('manufacturerName', part) ? (
								<div className="partPerStockLocationRowDisplay__rowInlineGroup">
									Manufacturer : {nullSafeGet('manufacturerName', part)}
								</div>
							) : null}
						</div>
					)}
					{nullSafeGet('description', part) && (
						<div className="partPerStockLocationRowDisplay__rowSubtitle">
							<Ellipsis tooltip={true} length={80}>
								{nullSafeGet('description', part)}
							</Ellipsis>
						</div>
					)}
					{nullSafeGet('partsPerStockLocation.stockLocation', partInServiceCall) && (
						<div className="partPerStockLocationRowDisplay__rowSubtitle">
							<Ellipsis tooltip={true} length={80}>
								{`Stock Location : ${nullSafeGetOrElse(
									'partsPerStockLocation.stockLocation.name',
									partInServiceCall,
									'--'
								)}`}
							</Ellipsis>
						</div>
					)}
					{nullSafeGet('location', partInServiceCall) && (
						<div className="partPerStockLocationRowDisplay__rowSubtitle">
							<Ellipsis tooltip={true} length={80}>
								{`Location : ${nullSafeGetOrElse('location.name', partInServiceCall, '--')}`}
							</Ellipsis>
						</div>
					)}
					<div className="partPerStockLocationRowDisplay__rowSubtitle">
						{`Service Call ${partWithServiceCall.serviceCallNumber}
						(${dateFormatter(serviceCall.createdAt, DATE_FORMAT)})`}
					</div>
					{nullSafeGet('trackingNumber', partInServiceCall) && (
						<div className="partPerStockLocationRowDisplay__rowSubtitle">
							<div className="flex flex-row items-center">
								{`Tracking No. - ${nullSafeGetOrElse('trackingNumber', partInServiceCall, '--')}`}
								{nullSafeGet('markShippedAt', partInServiceCall) && (
									<div className="ml-2">
										{`Shipped At : ${dateFormatter(
											nullSafeGetOrElse('markShippedAt', partInServiceCall, '--'),
											DATE_FORMAT
										)}`}
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
			<Row align="middle" gutter={16}>
				<Col className="partQuantity">{nullSafeGet('quantity', partInServiceCall)}</Col>
				{/* <Col>
					<Button onClick={checkAndOpenUpdateQuantityModal}>Edit</Button>
				</Col> */}
				<Col>
					<Popconfirm
						onConfirm={onConfirmRemove}
						title="Are you sure you want to remove this part?"
						okText="Yes"
						cancelText="No"
					>
						<DeleteOutlined style={{ marginLeft: 8 }} translate="" />
					</Popconfirm>
				</Col>
			</Row>

			{updateQuantityModalVisible && (
				<AddPartsModal
					serviceCall={serviceCall}
					partInServiceCall={partInServiceCall}
					successCallback={successCallback}
					onCancel={hideUpdateQuantityModal}
					openCreatePurchaseRequest={onCreatePR}
				/>
			)}
		</div>
	);
};

export default PartsWithQuantityRowDisplay;
